import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Settings,
  LogOut,
  Menu,
  X,
  User,
  BarChart3,
  BookOpen,
  AlertTriangle,
  Clock,
  Briefcase,
  KanbanSquare,
} from 'lucide-react'
import { clsx } from 'clsx'
import NotificationBell from '../common/NotificationBell'
import { usePermissions } from '../../hooks/usePermissions'

interface NavItem {
  name: string
  href: string
  icon: React.ElementType
  module?: string
}

// Company Admin navigation
const adminNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, module: 'admin_dashboard' },
  { name: 'Users', href: '/admin/users', icon: Users, module: 'admin_users' },
  { name: 'KPI Indicators', href: '/admin/indicators', icon: BarChart3, module: 'admin_indicators' },
  { name: 'Reports', href: '/admin/reports', icon: FileText, module: 'admin_reports' },
  { name: 'HR Management', href: '/admin/hr', icon: Briefcase, module: 'admin_hr' },
  { name: 'Learning Analytics', href: '/admin/learning', icon: BookOpen, module: 'admin_learning_analytics' },
  { name: 'Projects', href: '/admin/projects', icon: KanbanSquare, module: 'admin_project_monitoring' },
  { name: 'Company Settings', href: '/admin/settings', icon: Settings, module: 'admin_settings' },
]

// Superadmin navigation
const superadminNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/superadmin', icon: LayoutDashboard },
  { name: 'Companies', href: '/superadmin/companies', icon: Building2 },
]

export default function WebLayout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { canAccessPage } = usePermissions()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const isSuperadmin = user?.role === 'superadmin'
  const navItems = isSuperadmin
    ? superadminNavigation
    : adminNavigation.filter(item => !item.module || canAccessPage(item.module))
  const profilePath = isSuperadmin ? '/superadmin/profile' : '/admin/profile'

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-0 left-0 h-full w-64 bg-white border-r border-gray-200 z-50 transform transition-transform lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <h1 className="text-xl font-bold text-primary-600">{isSuperadmin ? 'EVO' : (user?.company?.name || 'EVO')}</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="border-t border-gray-200 p-4">
            <NavLink
              to={profilePath}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-2',
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )
              }
            >
              <User className="h-5 w-5" />
              Profile
            </NavLink>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 w-full"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex items-center gap-4 ml-auto">
              <NotificationBell />
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">
                  {user?.ev_designation_name || (user?.role === 'company_admin' ? 'Company Admin' : user?.role)}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Subscription Warning Banner */}
        {user?.subscription && user.subscription.status === 'expiring_soon' && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-yellow-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">
                  Subscription Expiring Soon
                </p>
                <p className="text-xs text-yellow-700">
                  Your subscription will expire in {user.subscription.days_until_expiry} days
                  {user.subscription.end_date && ` (${new Date(user.subscription.end_date).toLocaleDateString()})`}.
                  Please contact your administrator to renew.
                </p>
              </div>
            </div>
          </div>
        )}

        {user?.subscription && user.subscription.status === 'expired' && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  Subscription Expired
                </p>
                <p className="text-xs text-red-700">
                  Your company subscription has expired. Some features may be limited.
                  Please contact your administrator to renew.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
